import React, { useEffect, useState } from 'react';
import { api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Plus, Pencil, Trash2, Loader2, MapPin, Users, Navigation } from 'lucide-react';
import { toast } from 'sonner';

const SitesPage = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [formData, setFormData] = useState({
    nama: '',
    alamat: '',
    latitude: '',
    longitude: '',
    radius: 100,
  });

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const response = await api.get('/sites');
      setSites(response.data);
    } catch (error) {
      toast.error('Gagal memuat data site');
    } finally {
      setLoading(false);
    }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation tidak didukung browser');
      return;
    }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData((prev) => ({
          ...prev,
          latitude: position.coords.latitude.toFixed(7),
          longitude: position.coords.longitude.toFixed(7),
        }));
        setGettingLocation(false);
        toast.success('Lokasi saat ini berhasil diambil');
      },
      () => {
        toast.error('Gagal mendapatkan lokasi');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        nama: formData.nama,
        alamat: formData.alamat || null,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        radius: parseInt(formData.radius, 10) || 100,
      };
      if (selectedSite) {
        await api.put(`/sites/${selectedSite.id}`, payload);
        toast.success('Site berhasil diperbarui');
      } else {
        await api.post('/sites', payload);
        toast.success('Site berhasil ditambahkan');
      }
      setShowDialog(false);
      resetForm();
      fetchSites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan data');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSite) return;
    setSubmitting(true);

    try {
      await api.delete(`/sites/${selectedSite.id}`);
      toast.success('Site berhasil dihapus');
      setShowDeleteDialog(false);
      setSelectedSite(null);
      fetchSites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus data');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (site) => {
    setSelectedSite(site);
    setFormData({
      nama: site.nama,
      alamat: site.alamat || '',
      latitude: String(site.latitude),
      longitude: String(site.longitude),
      radius: site.radius,
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setSelectedSite(null);
    setFormData({ nama: '', alamat: '', latitude: '', longitude: '', radius: 100 });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sites-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope'] tracking-tight">
            Site
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Kelola lokasi kerja/klien. Site menentukan lokasi (geofence) absen karyawan.
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowDialog(true);
          }}
          className="gap-2"
          data-testid="add-site-btn"
        >
          <Plus className="w-4 h-4" />
          Tambah Site
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {sites.map((site, index) => (
          <Card
            key={site.id}
            className="animate-fade-in hover:shadow-md transition-shadow cursor-pointer"
            style={{ animationDelay: `${index * 50}ms` }}
            onClick={() => openEditDialog(site)}
            data-testid={`site-card-${site.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Users className="w-4 h-4" />
                  <span>{site.jumlah_karyawan}</span>
                </div>
              </div>
              <h3 className="font-semibold mt-3">{site.nama}</h3>
              <p className="text-xs text-muted-foreground">Radius {site.radius}m</p>
              {site.lokasi_tambahan && site.lokasi_tambahan.length > 0 && (
                <p
                  className="text-xs text-primary mt-1"
                  data-testid={`site-extra-locations-${site.id}`}
                >
                  +{site.lokasi_tambahan.length} titik lokasi tambahan
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nama Site</TableHead>
                  <TableHead>Alamat</TableHead>
                  <TableHead>Koordinat</TableHead>
                  <TableHead className="text-center">Radius</TableHead>
                  <TableHead className="text-center">Jumlah Karyawan</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sites.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <MapPin className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground">Belum ada site</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  sites.map((site) => (
                    <TableRow key={site.id} data-testid={`site-row-${site.id}`}>
                      <TableCell className="font-medium">{site.nama}</TableCell>
                      <TableCell className="text-muted-foreground max-w-xs truncate">
                        {site.alamat || '-'}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        <div>{site.latitude.toFixed(5)}, {site.longitude.toFixed(5)}</div>
                        {site.lokasi_tambahan && site.lokasi_tambahan.length > 0 && (
                          <div
                            className="text-primary mt-0.5"
                            data-testid={`site-extra-locations-row-${site.id}`}
                          >
                            +{site.lokasi_tambahan.length} lokasi tambahan
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">{site.radius}m</TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-sm font-medium">
                          <Users className="w-3.5 h-3.5" />
                          {site.jumlah_karyawan}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(site)}
                            data-testid={`edit-site-${site.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => {
                              setSelectedSite(site);
                              setShowDeleteDialog(true);
                            }}
                            data-testid={`delete-site-${site.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {selectedSite ? 'Edit Site' : 'Tambah Site Baru'}
            </DialogTitle>
            <DialogDescription>
              {selectedSite
                ? 'Perbarui informasi site & geofence absen'
                : 'Isi form berikut untuk menambahkan site/lokasi kerja baru'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nama">Nama Site *</Label>
              <Input
                id="nama"
                value={formData.nama}
                onChange={(e) => setFormData({ ...formData, nama: e.target.value })}
                placeholder="PUSDATIN, RSU PEKERJA, dll"
                required
                data-testid="form-nama-site"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="alamat">Alamat</Label>
              <Input
                id="alamat"
                value={formData.alamat}
                onChange={(e) => setFormData({ ...formData, alamat: e.target.value })}
                placeholder="Alamat lokasi kerja"
                data-testid="form-alamat-site"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="latitude">Latitude *</Label>
                <Input
                  id="latitude"
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  placeholder="-6.1620075"
                  required
                  data-testid="form-latitude"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="longitude">Longitude *</Label>
                <Input
                  id="longitude"
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  placeholder="106.8752317"
                  required
                  data-testid="form-longitude"
                />
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleUseCurrentLocation}
              disabled={gettingLocation}
              className="gap-2"
              data-testid="use-current-location-btn"
            >
              {gettingLocation ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Navigation className="w-4 h-4" />
              )}
              Gunakan Lokasi Saat Ini
            </Button>
            <div className="space-y-2">
              <Label htmlFor="radius">Radius Geofence (meter) *</Label>
              <Input
                id="radius"
                type="number"
                min="10"
                value={formData.radius}
                onChange={(e) => setFormData({ ...formData, radius: e.target.value })}
                required
                data-testid="form-radius"
              />
            </div>
            {selectedSite?.lokasi_tambahan && selectedSite.lokasi_tambahan.length > 0 && (
              <div
                className="rounded-md border border-primary/30 bg-primary/5 p-3 text-xs space-y-1"
                data-testid="site-extra-locations-info"
              >
                <p className="font-medium text-primary">
                  Lokasi tambahan (titik geofence lain untuk site ini):
                </p>
                {selectedSite.lokasi_tambahan.map((loc, idx) => (
                  <p key={idx} className="font-mono text-muted-foreground">
                    {loc.label || `Lokasi ${idx + 2}`}: {loc.latitude.toFixed(5)}, {loc.longitude.toFixed(5)} (radius {loc.radius}m)
                  </p>
                ))}
                <p className="text-muted-foreground italic">
                  Edit lokasi tambahan belum tersedia di UI ini, hubungi admin sistem.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Batal
              </Button>
              <Button type="submit" disabled={submitting} data-testid="form-submit-site">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Menyimpan...
                  </>
                ) : selectedSite ? (
                  'Perbarui'
                ) : (
                  'Simpan'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Hapus Site</DialogTitle>
            <DialogDescription>
              Apakah Anda yakin ingin menghapus site{' '}
              <strong>{selectedSite?.nama}</strong>? Site yang masih memiliki karyawan
              tidak dapat dihapus.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Batal
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={submitting}
              data-testid="confirm-delete-site"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Menghapus...
                </>
              ) : (
                'Hapus'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SitesPage;
